import React, { useState, useMemo } from 'react';
import { X, Copy, MessageCircle, Calendar, AlertTriangle, Ban, Trash2, FileDown, Loader2 } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import CriticalStockDocument from './CriticalStockDocument';

const STATUS_OPTIONS = [
    { id: 'OUT_OF_STOCK', label: 'אזל במלאי', icon: AlertTriangle, template: 'אזל במלאי.' },
    { id: 'RETURNS_JAN', label: 'עדכן כשיחזור', icon: Calendar, template: 'אעדכן כאשר יחזור למלאי.' },
    { id: 'DISCONTINUED', label: 'לא חוזר', icon: Ban, template: 'אזל במלאי ולא ישוב לגיוון.' },
    { id: 'IN_STOCK', label: 'קיים במלאי', icon: MessageCircle, template: 'קיים במלאי לאספקה מיידית.' },
];

const ReportGeneratorModal = ({ isOpen, onClose, selectedItems, onRemoveItem }) => {
    // Store only user-overridden statuses (not defaults)
    const [statusOverrides, setStatusOverrides] = useState({});
    const [customHeader, setCustomHeader] = useState('שלום, להלן עדכון מלאי על מוצרי AEG:');

    // Compute effective statuses: user overrides + defaults for items without overrides
    const itemStatuses = useMemo(() => {
        const statuses = {};
        selectedItems.forEach(item => {
            // Use override if exists, otherwise compute default
            statuses[item.SKU] = statusOverrides[item.SKU] ??
                (item.PhysicalStock <= 0 ? 'OUT_OF_STOCK' : 'IN_STOCK');
        });
        return statuses;
    }, [selectedItems, statusOverrides]);

    const handleStatusChange = (sku, statusId) => {
        setStatusOverrides(prev => ({ ...prev, [sku]: statusId }));
    };

    const generateReportText = () => {
        if (!selectedItems.length) return '';

        let text = `${customHeader}\n\n`;

        // Group by status
        const itemsByStatus = {};
        selectedItems.forEach(item => {
            const statusId = itemStatuses[item.SKU] || 'OUT_OF_STOCK';
            if (!itemsByStatus[statusId]) itemsByStatus[statusId] = [];
            itemsByStatus[statusId].push(item);
        });

        // Build Text
        Object.entries(itemsByStatus).forEach(([statusId, items]) => {
            const option = STATUS_OPTIONS.find(o => o.id === statusId);

            items.forEach(item => {
                text += `- *${item.SKU}* - ${option.template}\n\n`;
            });
        });

        return text;
    };

    const handleSendWhatsApp = () => {
        const text = generateReportText();
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generateReportText());
        // Could add a toast here
    };

    // שם קובץ ה-PDF
    const pdfFileName = `AEG_Critical_Stock_${new Date().toISOString().split('T')[0]}.pdf`;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm animate-fade-in" dir="rtl">
            <div className="bg-white w-full h-full md:h-[90vh] md:rounded-2xl md:shadow-2xl md:max-w-2xl flex flex-col overflow-hidden border border-gray-200 animate-slide-in rounded-t-2xl md:rounded-none">

                {/* Header */}
                <div className="bg-gray-900 p-6 text-white flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <MessageCircle className="text-green-400" />
                        <h2 className="text-xl font-bold">מחולל דוחות סטטוס</h2>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition">
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">

                    {/* Left Panel: Item Configuration */}
                    <div className="flex-1 overflow-y-auto p-6 border-l border-gray-100 bg-gray-50 custom-scrollbar">
                        <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                            <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                            הגדרת סטטוס לפריטים ({selectedItems.length})
                        </h3>

                        <div className="space-y-3">
                            {selectedItems.map(item => (
                                <div key={item.SKU} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:border-blue-300 transition-colors">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="font-bold text-gray-800 text-sm">{item.SKU}</div>
                                            <div className="text-xs text-gray-500 line-clamp-1">{item.Description}</div>
                                        </div>
                                        {item.PhysicalStock <= 0 && (
                                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded-full">חסר</span>
                                        )}
                                        <button
                                            onClick={() => onRemoveItem && onRemoveItem(item.SKU)}
                                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                            title="הסר מהדוח"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>

                                    <select
                                        value={itemStatuses[item.SKU] || ''}
                                        onChange={(e) => handleStatusChange(item.SKU, e.target.value)}
                                        className="w-full text-sm bg-white text-gray-900 border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        {STATUS_OPTIONS.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Panel: Preview */}
                    <div className="flex-1 flex flex-col p-6 bg-white shrink-0">
                        <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                            <span className="bg-gray-200 text-gray-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                            תצוגה מקדימה
                        </h3>

                        {/* Header Input */}
                        <div className="mb-4">
                            <label className="text-xs font-bold text-gray-500 mb-1 block">פתיח:</label>
                            <input
                                type="text"
                                value={customHeader}
                                onChange={(e) => setCustomHeader(e.target.value)}
                                className="w-full text-sm border-b border-gray-200 focus:border-blue-500 outline-none py-1 bg-transparent font-bold text-gray-800"
                            />
                        </div>

                        {/* Text Preview Box */}
                        <div className="flex-1 bg-[#dcf8c6]/20 border-2 border-dashed border-green-100 rounded-xl p-4 overflow-y-auto whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-800 custom-scrollbar relative">
                            {generateReportText()}
                        </div>

                        {/* Actions */}
                        <div className="mt-6 space-y-3">
                            <div className="flex gap-3">
                                <button
                                    onClick={handleSendWhatsApp}
                                    className="flex-1 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-green-200"
                                >
                                    <MessageCircle size={20} />
                                    שלח בוואטסאפ
                                </button>
                                <button
                                    onClick={handleCopy}
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Copy size={20} />
                                    העתק
                                </button>
                            </div>

                            {/* PDF Download Button */}
                            <PDFDownloadLink
                                document={<CriticalStockDocument items={selectedItems} />}
                                fileName={pdfFileName}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-red-200"
                            >
                                {({ loading }) => (
                                    loading ? (
                                        <>
                                            <Loader2 size={20} className="animate-spin" />
                                            מייצר PDF...
                                        </>
                                    ) : (
                                        <>
                                            <FileDown size={20} />
                                            הורד דוח PDF
                                        </>
                                    )
                                )}
                            </PDFDownloadLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportGeneratorModal;

